import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    BookRidePlan,
    BookRideSpecification,
    BookRideStatus,
    Feature,
    GeocodeAddress,
    GeocodeAddressType,
    ReturnWithPlan,
} from '../ride-planner.model';
import { RidePlannerService } from '../ride-planner.service';
import { RidePlannerActions } from './ride-planner.actions';
import { RidePlannerPartialStoreState } from './ride-planner.reducer';
import * as RidePlannerSelector from './ride-planner.selectors';
import { Product } from '@ridango/core';

@Injectable()
export class RidePlannerFacade {
    step$: Observable<number> = this.store.select(RidePlannerSelector.getStep);
    bookingStatus$: Observable<BookRideStatus> = this.store.select(RidePlannerSelector.getBookingStatus);
    bookingId$: Observable<number> = this.store.select(RidePlannerSelector.getBookingId);
    plan$: Observable<BookRidePlan> = this.store.select(RidePlannerSelector.getPlan);
    specification$: Observable<BookRideSpecification> = this.store.select(RidePlannerSelector.getSpecification);
    dateAndTime$: Observable<any> = this.store.select(RidePlannerSelector.getDateAndTime);
    isInvoice$: Observable<boolean> = this.store.select(RidePlannerSelector.getIsInvoice);
    estimatedPrice$: Observable<any> = this.store.select(RidePlannerSelector.getEstimatedPrice);
    products$: Observable<Product[]> = this.store.select(RidePlannerSelector.getProducts);
    priorityTime$: Observable<any> = this.store.select(RidePlannerSelector.getPriorityTime);
    passenger$: Observable<number> = this.store.select(RidePlannerSelector.getPassenger);
    productId$: Observable<string> = this.store.select(RidePlannerSelector.getProductId);
    additionalInformation$: Observable<string> = this.store.select(RidePlannerSelector.getAdditionalInformation);
    stops$: Observable<any> = this.store.select(RidePlannerSelector.getStops);
    error$: Observable<HttpErrorResponse> = this.store.select(RidePlannerSelector.getError);
    loading$: Observable<boolean> = this.store.select(RidePlannerSelector.getLoading);
    returnWithPlan$: Observable<ReturnWithPlan> = this.store.select(RidePlannerSelector.getIsReturnWithPlan);
    potentialStops$: Observable<GeocodeAddress[]> = this.store.select(RidePlannerSelector.getPotentialStops);
    filteredStops$: Observable<GeocodeAddress[]> = this.store.select(RidePlannerSelector.getFilteredStops);

    constructor(
        private service: RidePlannerService,
        private store: NgRedux<RidePlannerPartialStoreState>,
        private actions: RidePlannerActions
    ) {}

    addPlan(plan: BookRidePlan): void {
        this.store.dispatch(this.actions.setPlan(plan));
    }

    setStep(step: number): void {
        this.store.dispatch(this.actions.setStep(step));
    }

    /**
     * @deprecated in favour of fetchGeocodeAddress
     */
    fetchSuggestions(terms: string): Observable<Feature[]> {
        return this.service.fetchSuggestions(terms).pipe(map((response) => response.features));
    }

    /**
     * @deprecated in favour of fetchGeocodeAddressByCoordinates
     */
    getAddressByCoordinates(lat: number, lon: number) {
        return this.service.getAddressByCoordinates(lat, lon).pipe(map((response) => response.features[0]));
    }

    setPassenger(passenger: number) {
        this.store.dispatch(this.actions.setPassenger(passenger));
    }

    setProductId(productId: string) {
        this.store.dispatch(this.actions.setProductId(productId));
    }

    setEstimatedPrice(price: number) {
        this.store.dispatch(this.actions.setEstimatedPrice(price));
    }

    setAdditionalInformation(information: string) {
        this.store.dispatch(this.actions.setAdditionalInformation(information));
    }

    setIsInvoice(payment: boolean) {
        this.store.dispatch(this.actions.setIsInvoice(payment));
    }

    setIsReturn(isReturn: boolean) {
        this.store.dispatch(this.actions.setIsReturn(isReturn));
    }

    fetchEstimatedPrice(startDate: Date, coordinates: any) {
        this.store.dispatch(this.actions.fetchEstimatedPrice({ startDate, coordinates }));
    }

    fetchProducts() {
        this.store.dispatch(this.actions.fetchProducts());
    }

    postBookingFlow(booking: any) {
        this.store.dispatch(this.actions.postBookingFlow(booking));
    }

    resetSpecification() {
        this.store.dispatch(this.actions.resetSpecification());
    }

    fetchGeocodeAddress(terms: string, countrycode = 'fi'): Observable<GeocodeAddress[]> {
        return this.service.fetchLocationSuggestions(terms, countrycode).pipe(
            map((response) => {
                return this.service.mapFeaturesToGeocodeAddresses(response.features);
            })
        );
    }

    fetchGeocodeAddressEstonia(terms: string, includeBusStops = true): Observable<GeocodeAddress[]> {
        return this.service.fetchLocationSuggestionsEstonia(terms, includeBusStops).pipe(
            map((response) => {
                let list = response.data;

                if (includeBusStops) {
                    const convertedStops = response.stops.map((stop) => ({
                        id: stop.id,
                        address: stop.stop_name,
                        type: GeocodeAddressType.BUS_STOP,
                        lat: stop.stop_lat,
                        lon: stop.stop_lon,
                        geometry: [],
                    }));

                    list = [...response.data, ...convertedStops];
                }

                const sortedList = list.sort((a, b) => {
                    if (a.address.startsWith(terms) && b.address.startsWith(terms)) {
                        return a.address.localeCompare(b.address);
                    } else if (a.address.startsWith(terms)) {
                        return -1;
                    } else if (b.address.startsWith(terms)) {
                        return 1;
                    }

                    return a.address.localeCompare(b.address);
                });

                return sortedList;
            })
        );
    }

    fetchStopsByRegion(input: string): void {
        this.store.dispatch(this.actions.fetchPotentialStops(input));
    }

    fetchFilteredStopsByRegion(input: string): void {
        this.store.dispatch(this.actions.fetchFilteredPotentialStops(input));
    }

    fetchGeocodeAddressByCoordinatesEstonia(lat: number, lng: number): Observable<GeocodeAddress> {
        return this.service.fetchGeocodeAddressByCoordinatesEstonia(lat, lng).pipe(map((response) => response.data[0]));
    }

    fetchGeocodeAddressByCoordinates(lat: number, lng: number): Observable<any> {
        return this.service
            .fetchGeocodeAddressByCoordinates(lat, lng)
            .pipe(map((response) => response.features[0].properties));
    }

    linkOrderWithShoppingCart(bookingId: number, cartId: number): Observable<any> {
        return this.service.linkOrderWithShoppingCart(bookingId, cartId);
    }
}
